import React, { Fragment } from 'react';
import styled from '@emotion/styled';
import { Accordion, Button, Card, ProgressBar } from 'react-bootstrap';
import { MdCheckCircle, MdErrorOutline, MdInfo, MdWarning } from 'react-icons/md';

export const CustomLegend = ({ subTitle, payload = [] }) => (
    <div>
        <div style={{ textAlign: 'center', marginBottom: '10px' }}>{subTitle}</div>
        {/* <div style={{ display: 'flex', justifyContent: 'center', gap: '20px' }}>
      {payload.map((entry, index) => (
        <div key={`item-${index}`} style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ 
            width: '10px', 
            height: '10px', 
            backgroundColor: entry.color, 
            marginRight: '5px' 
          }} />
          <span>{entry.value}</span>
        </div>
      ))}
    </div> */}
    </div>
);

export const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div
                style={{
                    backgroundColor: 'white',
                    padding: '12px',
                    border: '1px solid #ccc',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    maxWidth: '80vw',
                    maxHeight: '80vh',
                    overflowY: 'auto',
                    zIndex: 1000,
                }}
            >
                <p
                    style={{
                        margin: '0 0 8px 0',
                        fontWeight: 'bold',
                        borderBottom: '1px solid #eee',
                        paddingBottom: '4px',
                        position: 'sticky',
                        top: 0,
                        backgroundColor: 'white',
                    }}
                >
                    {label}
                </p>
                {payload.map((entry, index) => (
                    <p
                        key={index}
                        style={{
                            color: entry.color,
                            margin: '4px 0',
                            fontSize: '14px',
                            lineHeight: '1.4',
                        }}
                    >
                        {entry.name}: <strong>{entry.value}</strong>
                    </p>
                ))}
            </div>
        );
    }
    return null;
};

const formatDate = (dateString) => {
    return new Date(dateString || null).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

export const ChartDetailsFooter = ({ chart }) => {
    const requestParamters = chart?.requestParamters || {};

    return (
        <Fragment>
            <Card.Footer className="p-0">
                <Accordion>
                    <Accordion.Toggle as={Button} eventKey="prompt" className="w-100 text-left p-3">
                        <div className="ml-4">
                            <MdInfo className="mr-2" /> Chart Details
                        </div>
                    </Accordion.Toggle>
                    <Accordion.Collapse eventKey="prompt">
                        <Card.Body>
                            {chart?.createdAt && (
                                <div className="mb-2">
                                    <strong>Created:</strong> {formatDate(chart?.createdAt)}
                                </div>
                            )}
                            {requestParamters.timeRange && (
                                <div>
                                    <strong>Time Range:</strong> {requestParamters.timeRange}
                                </div>
                            )}
                            {requestParamters.formName && (
                                <div>
                                    <strong>Form Name:</strong> {requestParamters.formName}
                                </div>
                            )}

                            {chart.prompt && (
                                <div>
                                    <strong>Prompt:</strong> {chart.prompt}
                                </div>
                            )}
                            {/* {JSON.stringify(chart?.requestParamters || {})} */}
                        </Card.Body>
                    </Accordion.Collapse>
                </Accordion>
            </Card.Footer>

            {chart.validation && (
                <Card.Footer className="p-0">
                    <Accordion>
                        <Accordion.Toggle as={Button} eventKey="validation" className="w-100 text-left p-3">
                            <div className="ml-4">
                                <MdInfo className="mr-2" /> Data Quality Information
                            </div>
                        </Accordion.Toggle>
                        <Accordion.Collapse eventKey="validation">
                            <Card.Body>
                                <ValidationDisplay validation={chart.validation} />
                            </Card.Body>
                        </Accordion.Collapse>
                    </Accordion>
                </Card.Footer>
            )}
        </Fragment>
    );
};

const ValidationPanel = styled.div`
    background: ${(props) => {
        switch (props.status) {
            case 'sufficient':
                return '#e0f0ef';
            case 'partial':
                return '#fff3cd';
            case 'insufficient':
                return '#f8d7da';
            default:
                return '#f8f9fa';
        }
    }};
    border-radius: 10px;
    padding: 15px;
    margin-top: 15px;
    margin-bottom: 15px;
    border-left: 4px solid
        ${(props) => {
            switch (props.status) {
                case 'sufficient':
                    return '#50AEB0';
                case 'partial':
                    return '#ffc107';
                case 'insufficient':
                    return '#dc3545';
                default:
                    return '#6c757d';
            }
        }};
`;

export const ValidationDisplay = ({ validation }) => {
    if (!validation) return null;

    const { status, message, recommendations = [], dataCollectionRecommendations = [] } = validation;

    const getStatusIcon = () => {
        switch (status) {
            case 'sufficient':
                return <MdCheckCircle size={24} className="text-success mr-2" />;
            case 'partial':
                return <MdWarning size={24} className="text-warning mr-2" />;
            case 'insufficient':
                return <MdErrorOutline size={24} className="text-danger mr-2" />;
            default:
                return <MdInfo size={24} className="text-info mr-2" />;
        }
    };

    const getStatusVariant = () => {
        switch (status) {
            case 'sufficient':
                return 'success';
            case 'partial':
                return 'warning';
            case 'insufficient':
                return 'danger';
            default:
                return 'info';
        }
    };

    return (
        <ValidationPanel status={status}>
            <div className="d-flex align-items-center mb-2">
                {getStatusIcon()}
                <h5 className="mb-0">{message}</h5>
            </div>

            {status !== 'sufficient' && (
                <>
                    <hr />
                    <h6>Recommendations:</h6>
                    <ul className="mb-0">
                        {[...recommendations, ...dataCollectionRecommendations].map((rec, index) => (
                            <li key={index}>{rec}</li>
                        ))}
                    </ul>
                </>
            )}

            {validation.details && validation.details.dataPoints !== undefined && (
                <div className="mt-3">
                    <small className="text-muted d-block mb-1">
                        Data points: {validation.details.dataPoints}
                        {validation.details.minRequired && ` (minimum: ${validation.details.minRequired})`}
                        {validation.details.recommended && ` (recommended: ${validation.details.recommended})`}
                    </small>
                    <ProgressBar
                        now={Math.min(
                            100,
                            (validation.details.dataPoints / (validation.details.recommended || 20)) * 100
                        )}
                        variant={getStatusVariant()}
                    />
                </div>
            )}
        </ValidationPanel>
    );
};
